import { useCallback, useEffect, useState } from "react";
import { Bookmark, BookmarkCheck, Plus, Trash2 } from "lucide-react";
import Popover from "./Popover";

/* Vues enregistrées : recherche + filtres + tri + colonnes + densité.
   Stockage local volontaire — une vue est une préférence de poste de travail,
   pas une donnée d'administration à répliquer côté serveur. */
const prefix = key => `perform.admin.views.${key}`;

export function readViews(key) {
  try {
    const raw = window.localStorage.getItem(prefix(key));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeViews(key, views) {
  try {
    window.localStorage.setItem(prefix(key), JSON.stringify(views));
  } catch {
    /* Stockage indisponible (navigation privée) : la vue reste en mémoire. */
  }
}

export default function SavedViews({ storageKey, current, onApply, builtins = [] }) {
  const [views, setViews] = useState(() => readViews(storageKey));
  const [activeId, setActiveId] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => { setViews(readViews(storageKey)); setActiveId(null); }, [storageKey]);

  const persist = useCallback(next => { setViews(next); writeViews(storageKey, next); }, [storageKey]);

  const save = close => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const view = { id: `v-${Date.now()}`, name: trimmed, view: current };
    persist([...views, view]);
    setActiveId(view.id);
    setName("");
    close();
  };

  const apply = (entry, close) => {
    setActiveId(entry.id);
    onApply(entry.view);
    close();
  };

  const remove = entry => {
    persist(views.filter(v => v.id !== entry.id));
    if (activeId === entry.id) setActiveId(null);
  };

  const activeName = [...builtins, ...views].find(v => v.id === activeId)?.name;

  return (
    <Popover
      label={activeName || "Vues"}
      icon={activeName ? BookmarkCheck : Bookmark}
      testid="saved-views"
      title="Vues enregistrées"
      width={278}
    >
      {({ close }) => (
        <>
          {builtins.length > 0 && <div className="adm-menu-head"><span>Vues fournies</span></div>}
          {builtins.map(entry => (
            <button
              key={entry.id}
              type="button"
              className={`adm-menu-item${activeId === entry.id ? " is-active" : ""}`}
              onClick={() => apply(entry, close)}
              data-testid={`view-apply-${entry.id}`}
            >
              <span>{entry.name}</span>
            </button>
          ))}
          <div className="adm-menu-head"><span>Mes vues</span></div>
          {views.length === 0 && <div className="adm-menu-empty">Aucune vue enregistrée sur ce poste.</div>}
          {views.map(entry => (
            <div key={entry.id} className="adm-menu-row">
              <button
                type="button"
                className={`adm-menu-item${activeId === entry.id ? " is-active" : ""}`}
                onClick={() => apply(entry, close)}
                data-testid={`view-apply-${entry.id}`}
              >
                <span>{entry.name}</span>
              </button>
              <button
                type="button"
                className="adm-btn is-sm is-ghost is-icon"
                aria-label={`Supprimer la vue ${entry.name}`}
                onClick={() => remove(entry)}
                data-testid={`view-delete-${entry.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          <div className="adm-menu-sep" />
          <div style={{ display: "flex", gap: 6, padding: "2px 4px 4px" }}>
            <input
              className="adm-input"
              placeholder="Nom de la vue"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") save(close); }}
              aria-label="Nom de la nouvelle vue"
              data-testid="view-name"
            />
            <button
              type="button"
              className="adm-btn is-sm"
              disabled={!name.trim()}
              onClick={() => save(close)}
              data-testid="view-save"
            >
              <Plus size={12} />Enregistrer
            </button>
          </div>
        </>
      )}
    </Popover>
  );
}
